import { useState, useEffect, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import {
    RiUserLine,
    RiSettingsLine,
    RiLogoutBoxRLine,
    RiLockPasswordLine,
    RiShieldCheckLine,
    RiMailLine,
    RiUser3Line,
    RiEditLine
} from 'react-icons/ri'

import { Button, Spinner } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { sanitizeUserProfile } from '@/lib/sanitize'
import type { UserDetails } from '@/types/auth'
import styles from './Profile.module.css'

export function Profile() {
    const navigate = useNavigate()
    const { logOut, getUser } = useAuthStore()
    const [activeTab, setActiveTab] = useState('profile')
    const [userDetails, setUserDetails] = useState<UserDetails | undefined>(() => getUser() ?? undefined)
    const [loggingOut, setLoggingOut] = useState(false)

    useEffect(() => {
        setUserDetails(getUser() ?? undefined)
    }, [getUser])

    // Sanitize user data before display
    const sanitizedProfile = useMemo(() => {
        if (!userDetails) return null;
        return sanitizeUserProfile(userDetails);
    }, [userDetails]);

    const handleLogout = async () => {
        setLoggingOut(true)
        await logOut()
        navigate('/')
    }

    const handleChangePassword = () => {
        navigate(ROUTES.changePassword)
    }

    const handleSetup2FA = () => {
        toast.error("Not available yet")
    }

    const handleEditProfile = () => {
        toast.error('Not available yet')
    }

    if (!userDetails || !sanitizedProfile) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <p>Error al cargar los datos del perfil</p>
                    <Button onClick={() => navigate(ROUTES.login)}>Ir al inicio de sesión</Button>
                </div>
            </div>
        )
    }

    const displayName = sanitizedProfile.username || sanitizedProfile.email?.split('@')[0] || 'Usuario'
    const initials = displayName.charAt(0).toUpperCase()

    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                {/* Sidebar */}
                <aside className={styles.sidebar}>
                    <div className={styles.avatarSection}>
                        <div className={styles.avatar}>
                            <span>{initials}</span>
                        </div>
                        <h2 className={styles.userName}>{displayName}</h2>
                        <p className={styles.userEmail}>{sanitizedProfile.email}</p>
                    </div>

                    <nav className={styles.nav}>
                        <button
                            className={`${styles.navButton} ${activeTab === 'profile' ? styles.active : ''}`}
                            onClick={() => setActiveTab('profile')}
                        >
                            <RiUserLine />
                            <span>Perfil</span>
                        </button>
                        <button
                            className={`${styles.navButton} ${activeTab === 'settings' ? styles.active : ''}`}
                            onClick={() => setActiveTab('settings')}
                        >
                            <RiSettingsLine />
                            <span>Configuración</span>
                        </button>
                    </nav>

                    <div className={styles.sidebarFooter}>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleLogout}
                            disabled={loggingOut}
                            className={styles.logoutButton}
                        >
                            {loggingOut ? (
                                <Spinner size="sm" variant="primary" />
                            ) : (
                                <>
                                    <RiLogoutBoxRLine />
                                    Cerrar Sesión
                                </>
                            )}
                        </Button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className={styles.main}>
                    {activeTab === 'profile' && (
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h1 className={styles.sectionTitle}>Información del Perfil</h1>
                                <Button variant="secondary" size="sm" onClick={handleEditProfile}>
                                    <RiEditLine />
                                    Editar
                                </Button>
                            </div>

                            <div className={styles.card}>
                                <div className={styles.fieldGroup}>
                                    <div className={styles.field}>
                                        <div className={styles.fieldIcon}>
                                            <RiUser3Line />
                                        </div>
                                        <div className={styles.fieldContent}>
                                            <label className={styles.fieldLabel}>Nombre de usuario</label>
                                            <p className={styles.fieldValue}>{sanitizedProfile.username || '-'}</p>
                                        </div>
                                    </div>
                                    ...
                                    <div className={styles.field}>
                                        <div className={styles.fieldIcon}>
                                            <RiMailLine />
                                        </div>
                                        <div className={styles.fieldContent}>
                                            <label className={styles.fieldLabel}>Correo Electrónico</label>
                                            <p className={styles.fieldValue}>{sanitizedProfile.email}</p>
                                        </div>
                                    </div>

                                    <div className={styles.field}>
                                        <div className={styles.fieldIcon}>
                                            <RiUserLine />
                                        </div>
                                        <div className={styles.fieldContent}>
                                            <label className={styles.fieldLabel}>Nombre</label>
                                            <p className={styles.fieldValue}>{sanitizedProfile.first_name || '-'}</p>
                                        </div>
                                    </div>

                                    <div className={styles.field}>
                                        <div className={styles.fieldIcon}>
                                            <RiUserLine />
                                        </div>
                                        <div className={styles.fieldContent}>
                                            <label className={styles.fieldLabel}>Apellido</label>
                                            <p className={styles.fieldValue}>{sanitizedProfile.last_name || '-'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeTab === 'settings' && (
                        <section className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <h1 className={styles.sectionTitle}>Ajustes de Seguridad</h1>
                            </div>

                            <div className={styles.card}>
                                <div className={styles.settingItem}>
                                    <div className={styles.settingIcon}>
                                        <RiLockPasswordLine />
                                    </div>
                                    <div className={styles.settingContent}>
                                        <h3 className={styles.settingTitle}>Cambiar Contraseña</h3>
                                        <p className={styles.settingDescription}>Actualice su contraseña periódicamente para mayor seguridad</p>
                                    </div>
                                    <Button variant="secondary" size="sm" onClick={handleChangePassword}>
                                        Cambiar
                                    </Button>
                                </div>

                                <div className={styles.divider} />

                                <div className={styles.settingItem}>
                                    <div className={styles.settingIcon}>
                                        <RiShieldCheckLine />
                                    </div>
                                    <div className={styles.settingContent}>
                                        <h3 className={styles.settingTitle}>Autenticación de dos factores</h3>
                                        <p className={styles.settingDescription}>Agregue una capa extra de seguridad a su cuenta</p>
                                    </div>
                                    <Button variant="primary" size="sm" onClick={handleSetup2FA}>
                                        Configurar
                                    </Button>
                                </div>
                            </div>
                        </section>
                    )}
                </main>
            </div>
        </div>
    )
}

